import React, { useCallback, useEffect, useRef, useState } from "react";
import { FlatList, Linking } from "react-native";
import { Box, EmptyJouvie, MainText } from "app/components/common";
import i18n from "@microdoing/shared/i18n";
import ResourceBox from "./ResourceBox";
import { connect, useDispatch, useSelector } from "react-redux";
import {
  getCoachingResourcesRequest,
  visitResourceRequest,
} from "@microdoing/shared/services/coaching/actions";
import {
  getCoachingResourcesWithDetails,
  getCoachingSessionId,
  getCoachingSessionResources,
} from "@microdoing/shared/services/coaching/selectors";
import { useNavigation } from "@react-navigation/native";
import { getUserId } from "@microdoing/shared/services/user/selectors";
import FeedbackModal from "./FeedbackModal";
import { getScrollTop } from "@microdoing/shared/services/nav/selectors";
import { changeNavField } from "@microdoing/shared/services/nav/actions";
import formateDoneDate, { realDate } from "../../utils/formatDoneDate";
import * as Animatable from "react-native-animatable";

const Library = ({
  requesting = false,
  coachingSessionResources,
  coachingSessionId,
  userId,
  coachingResources,
}) => {
  const [isFeedbackModalVisible, openFeedbackModal] = useState(false);
  const [selectedResource, setSelectResource] = useState({});
  const navigation = useNavigation();

  const scrollRef = useRef(null);
  const dispatch = useDispatch();

  const scrollTop = useSelector(getScrollTop);

  useEffect(() => {
    dispatch(getCoachingResourcesRequest());
  }, [coachingSessionResources, getCoachingResourcesRequest]);

  useEffect(() => {
    if (scrollTop) {
      scrollToTop();
      dispatch(changeNavField({ scrollTop: false }));
    }
  }, [scrollTop, dispatch]);

  const scrollToTop = () => {
    if (scrollRef && scrollRef.current) {
      scrollRef.current.scrollToIndex({ animated: true, index: 0 });
    }
  };

  const onRefresh = () => {
    dispatch(getCoachingResourcesRequest());
  };

  const keyExtractor = useCallback(item => item._id, []);

  const onPressBox = useCallback(
    (resourceId, link, rating) => () => {
      Linking.openURL(link);
      dispatch(visitResourceRequest({ resourceId, coachingSessionId, userId }));
      if (!rating) {
        setTimeout(() => {
          openFeedbackModal(true);
          setSelectResource({
            resourceId,
            link,
          });
        }, 250);
      }
    },
    [visitResourceRequest, coachingSessionId, userId],
  );

  const renderItem = useCallback(
    ({ item, index }) => {
      const {
        title,
        date,
        type,
        link,
        totalViews = 0,
        isVisited,
        rating,
      } = item;
      const diff = realDate(date);
      const previousDiff =
        index !== 0
          ? realDate(coachingResources[index - 1].date)
          : diff !== 0
          ? 0
          : 1;
      return (
        <Box marginVertical="xs" key={index}>
          {diff !== previousDiff && (
            <Animatable.View useNativeDriver delay={50} animation="fadeInUp">
              <MainText
                variant="l3-semibold"
                color="neutral.500"
                marginBottom="xxs"
              >
                {formateDoneDate(date)}
              </MainText>
            </Animatable.View>
          )}
          <ResourceBox
            {...item}
            // key={item._id}
            title={title}
            type={type}
            link={link}
            isVisited={totalViews > 0 || isVisited}
            onPress={onPressBox(item._id, link, rating)}
          />
        </Box>
      );
    },
    [onPressBox],
  );
  return (
    <Box flex={1}>
      <FeedbackModal
        isCoachingFeedback={true}
        resourceId={selectedResource?.resourceId}
        link={selectedResource?.link}
        isVisible={isFeedbackModalVisible}
        onPressModal={() => openFeedbackModal(false)}
      />
      {coachingResources.length < 1 && (
        <EmptyJouvie
          onPress={() => navigation.navigate("Home")}
          firstMessage={i18n.t("no-resources")}
          secondMessage={i18n.t("go-challenges")}
        />
      )}
      <FlatList
        ref={scrollRef}
        onRefresh={onRefresh}
        refreshing={requesting}
        keyboardShouldPersistTaps="always"
        data={coachingResources}
        extraData={null}
        horizontal={false}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
      />
    </Box>
  );
};

const mapStateToProps = state => {
  return {
    requesting: state.coaching.resourcesRequesting,
    coachingSessionResources: getCoachingSessionResources(state),
    coachingResources: getCoachingResourcesWithDetails(state),
    userId: getUserId(state),
    coachingSessionId: getCoachingSessionId(state),
  };
};

export default connect(mapStateToProps, {
  getCoachingResourcesRequest,
  visitResourceRequest,
})(Library);
